import React, { useReducer } from 'react';
import {
  Button,
  Dialog,
  DatePicker,
  Select,
  TextField,
} from '../../../../../components';
import { RestartAlt } from '@mui/icons-material';
import { downloadFile } from '../../../../../utils/downloadFile';
import dayjs, { type Dayjs } from 'dayjs';
import {
  useGetSupersedeData,
  useReleaseSupersededOffer,
  useSupersedeOffer,
} from '../../../graphql';

type GSSupersedeOfferDialogProps = {
  applicationId: string;
  open: boolean;
  onClose: () => void;
};

const DIALOG_TEXT = {
  TITLE: 'Supersede Offer',
  HINT: 'Superseded offer will not be visible to applicants until it is released. Leave the field blank if no update is needed.',
  ENTRY_YEAR: 'Entry Year',
  ENTRY_YEAR_HINT: 'For defer offer, the new entry year.',
  SUPERSEDE_NOTES: 'Supersede Notes',
  SUPERSEDE_NOTES_HINT:
    'This letter supersedes the one dated: (format example: 1 January 2025).',
  CONDITION_DEADLINE: 'Meet Condition Deadline',
  CONDITION_DEADLINE_HINT: 'For defer offer, change meet condition deadline.',
  SUPERSEDE_BUTTON: 'Supersede',
  RELEASE_BUTTON: 'Release',
  DELETE_BUTTON: 'Delete Superseded Offer',
  CLEAR_BUTTON: 'Clear Form',
  CURRENT_FILE: 'Current NoO',
  SUPERSEDED_FILE: 'Superseded NoO (Not yet released)',
  GENERAL_ERROR_TEXT: 'An unknown error occurred.',
  DOWNLOAD_ERROR_TEXT: 'Failed to download file.',
  SUPERSEDE_ERROR_TEXT: 'Failed to supersede NoO.',
  RELEASE_ERROR_TEXT: 'Failed to release NoO.',
  DELETE_ERROR_TEXT: 'Failed to delete supersede offer.',
  WHETHER_TO_SEND_EMAIL: 'Send update notes via email to applicant.',
  UPDATE_NOTE_TEXT: 'Update Notes',
  NO_UPDATE_NOTES_ERROR: 'Please fill the Update Notes for email notification.',
  LOADING: 'Loading...',
};

type DialogState = {
  supersedeLoading: boolean;
  releaseLoading: boolean;
  deleteLoading: boolean;
  error: string | null;
  entryYear: string;
  supersedeNotes: string;
  conditionDeadline: Dayjs | null;
  updateNotes: string;
  sendEmail: boolean;
};

enum DialogActionType {
  SET_SUPERSEDE_LOADING = 'SET_SUPERSEDE_LOADING',
  SET_RELEASE_LOADING = 'SET_RELEASE_LOADING',
  SET_DELETE_LOADING = 'SET_DELETE_LOADING',
  SET_ERROR = 'SET_ERROR',
  SET_ENTRY_YEAR = 'SET_ENTRY_YEAR',
  SET_SUPERSEDE_NOTES = 'SET_SUPERSEDE_NOTES',
  SET_CONDITION_DEADLINE = 'SET_CONDITION_DEADLINE',
  SET_UPDATE_NOTES = 'SET_UPDATE_NOTES',
  SET_SEND_EMAIL = 'SET_SEND_EMAIL',
  RESET_STATE = 'RESET_STATE',
}

type DialogAction =
  | { type: DialogActionType.SET_SUPERSEDE_LOADING; payload: boolean }
  | { type: DialogActionType.SET_RELEASE_LOADING; payload: boolean }
  | { type: DialogActionType.SET_DELETE_LOADING; payload: boolean }
  | { type: DialogActionType.SET_ERROR; payload: string | null }
  | { type: DialogActionType.SET_ENTRY_YEAR; payload: string }
  | { type: DialogActionType.SET_SUPERSEDE_NOTES; payload: string }
  | { type: DialogActionType.SET_CONDITION_DEADLINE; payload: Dayjs | null }
  | { type: DialogActionType.SET_UPDATE_NOTES; payload: string }
  | { type: DialogActionType.SET_SEND_EMAIL; payload: boolean }
  | { type: DialogActionType.RESET_STATE };

const reducer = (state: DialogState, action: DialogAction): DialogState => {
  switch (action.type) {
    case DialogActionType.SET_SUPERSEDE_LOADING:
      return { ...state, supersedeLoading: action.payload };
    case DialogActionType.SET_RELEASE_LOADING:
      return { ...state, releaseLoading: action.payload };
    case DialogActionType.SET_DELETE_LOADING:
      return { ...state, deleteLoading: action.payload };
    case DialogActionType.SET_ERROR:
      return { ...state, error: action.payload };
    case DialogActionType.SET_ENTRY_YEAR:
      return { ...state, entryYear: action.payload };
    case DialogActionType.SET_SUPERSEDE_NOTES:
      return { ...state, supersedeNotes: action.payload };
    case DialogActionType.SET_CONDITION_DEADLINE:
      return { ...state, conditionDeadline: action.payload };
    case DialogActionType.SET_UPDATE_NOTES:
      return { ...state, updateNotes: action.payload };
    case DialogActionType.SET_SEND_EMAIL:
      return { ...state, sendEmail: action.payload };
    case DialogActionType.RESET_STATE:
      return {
        supersedeLoading: false,
        releaseLoading: false,
        deleteLoading: false,
        error: null,
        entryYear: '',
        supersedeNotes: '',
        conditionDeadline: null,
        sendEmail: true,
        updateNotes: '',
      };
    default:
      return state;
  }
};

const GSSupersedeOfferDialog: React.FC<GSSupersedeOfferDialogProps> = ({
  applicationId,
  open,
  onClose,
}) => {
  const [state, dispatch] = useReducer(reducer, {
    supersedeLoading: false,
    releaseLoading: false,
    deleteLoading: false,
    error: null,
    entryYear: '',
    supersedeNotes: '',
    conditionDeadline: null,
    sendEmail: true,
    updateNotes: '',
  });

  const { supersedeData, loading, refetch } =
    useGetSupersedeData(applicationId);

  const entryYearList = supersedeData?.entryYearList ?? [];
  const currentFile = supersedeData?.fileChecks?.[0]?.file ?? null;
  const supersededFile = supersedeData?.supersededFile?.file ?? null;

  const supersedeOffer = useSupersedeOffer();
  const releaseSupersedeOffer = useReleaseSupersededOffer();

  const handleDownload = async (
    fileId: string,
    fileName: string,
  ): Promise<void> => {
    try {
      await downloadFile(fileId, 'pdf', fileName);
    } catch (error) {
      dispatch({
        type: DialogActionType.SET_ERROR,
        payload: `${DIALOG_TEXT.DOWNLOAD_ERROR_TEXT}. ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    }
  };

  const handleSupersede = async (): Promise<void> => {
    dispatch({ type: DialogActionType.SET_SUPERSEDE_LOADING, payload: true });
    dispatch({ type: DialogActionType.SET_ERROR, payload: null });
    try {
      await supersedeOffer({
        variables: {
          input: {
            applicationId,
            targetEntryYear: state.entryYear,
            supersedeOfferDate: state.supersedeNotes,
            meetConditionDeadline: state.conditionDeadline,
          },
        },
      });
      refetch();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : DIALOG_TEXT.GENERAL_ERROR_TEXT;
      dispatch({
        type: DialogActionType.SET_ERROR,
        payload: `${DIALOG_TEXT.SUPERSEDE_ERROR_TEXT} ${errorMessage}`,
      });
    } finally {
      dispatch({
        type: DialogActionType.RESET_STATE,
      });
    }
  };

  const handleRelease = async (): Promise<void> => {
    if (state.sendEmail && !state.updateNotes) {
      dispatch({
        type: DialogActionType.SET_ERROR,
        payload: DIALOG_TEXT.NO_UPDATE_NOTES_ERROR,
      });
      return;
    }
    dispatch({ type: DialogActionType.SET_RELEASE_LOADING, payload: true });
    dispatch({ type: DialogActionType.SET_ERROR, payload: null });
    try {
      await releaseSupersedeOffer({
        variables: {
          input: { applicationId, updateNotes: state.updateNotes },
        },
      });
      refetch();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : DIALOG_TEXT.GENERAL_ERROR_TEXT;
      dispatch({
        type: DialogActionType.SET_ERROR,
        payload: `${DIALOG_TEXT.RELEASE_ERROR_TEXT}. ${errorMessage}`,
      });
    } finally {
      dispatch({
        type: DialogActionType.RESET_STATE,
      });
    }
  };

  const handleClearForm = (): void => {
    dispatch({ type: DialogActionType.RESET_STATE });
  };

  return (
    <Dialog
      openDialog={open}
      onDialogClose={() => {
        dispatch({ type: DialogActionType.RESET_STATE });
        onClose();
      }}
      displayCloseButton={true}
      dialogTitle={DIALOG_TEXT.TITLE}
      fullWidth
      width="80%"
    >
      <div className="px-6">
        <div className="text-left pt-4 pb-2">{DIALOG_TEXT.HINT}</div>
        {state.error !== null && (
          <div className="text-red-500 text-left pt-2 pb-4">{state.error}</div>
        )}
        {loading ? (
          <div className="text-center py-4">Loading data...</div>
        ) : (
          <div className="flex flex-row gap-8 py-4">
            <div className="flex-1">
              <div className="text-lg font-bold">
                {DIALOG_TEXT.CURRENT_FILE}
              </div>
              {currentFile ? (
                <div>
                  <button
                    onClick={async () => {
                      await handleDownload(currentFile.id, currentFile.name);
                    }}
                    className="text-blue-500 underline"
                  >
                    {currentFile.name}
                  </button>
                </div>
              ) : (
                <div>No current file available.</div>
              )}
              {loading ? (
                <div className="text-center py-4">Loading data...</div>
              ) : (
                <div>
                  <div className="py-4">
                    <div className="text-left pb-2">
                      {DIALOG_TEXT.ENTRY_YEAR_HINT}
                    </div>
                    <Select
                      label={DIALOG_TEXT.ENTRY_YEAR}
                      value={state.entryYear}
                      options={entryYearList}
                      onChange={e => {
                        dispatch({
                          type: DialogActionType.SET_ENTRY_YEAR,
                          payload: e.target.value,
                        });
                      }}
                      fullWidth
                    ></Select>
                  </div>
                  <div className="py-4">
                    <div className="text-left pb-2">
                      {DIALOG_TEXT.SUPERSEDE_NOTES_HINT}
                    </div>
                    <DatePicker
                      label={DIALOG_TEXT.SUPERSEDE_NOTES}
                      value={
                        state.supersedeNotes
                          ? dayjs(state.supersedeNotes, 'D MMMM YYYY')
                          : null
                      }
                      onChange={(date: any) => {
                        const formattedDate = date
                          ? date.format('D MMMM YYYY')
                          : '';
                        dispatch({
                          type: DialogActionType.SET_SUPERSEDE_NOTES,
                          payload: formattedDate,
                        });
                      }}
                      format="D MMMM YYYY"
                      shouldRespectLeadingZeros={true}
                      className="w-full"
                    />
                  </div>
                  <div className="py-4">
                    <div className="text-left pb-2">
                      {DIALOG_TEXT.CONDITION_DEADLINE_HINT}
                    </div>
                    <DatePicker
                      label={DIALOG_TEXT.CONDITION_DEADLINE}
                      value={state.conditionDeadline}
                      onChange={(date: any) => {
                        const adjustedDate = dayjs(date)
                          .hour(23)
                          .minute(59)
                          .second(59)
                          .utcOffset(8);
                        dispatch({
                          type: DialogActionType.SET_CONDITION_DEADLINE,
                          payload: adjustedDate,
                        });
                      }}
                      className="w-full"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="text-lg font-bold">
                {DIALOG_TEXT.SUPERSEDED_FILE}
              </div>
              {supersededFile ? (
                <div>
                  <button
                    onClick={async () => {
                      await handleDownload(
                        supersededFile.id,
                        supersededFile.name,
                      );
                    }}
                    className="text-blue-500 underline"
                  >
                    {supersededFile.name}
                  </button>
                </div>
              ) : (
                <div>No superseded file available.</div>
              )}
              <div className="py-4">
                <div className="pb-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="sendEmailCheckbox"
                      style={{ width: '16px', height: '16px' }}
                      checked={state.sendEmail}
                      onChange={e => {
                        dispatch({
                          type: DialogActionType.SET_SEND_EMAIL,
                          payload: e.target.checked,
                        });

                        if (!e.target.checked) {
                          dispatch({
                            type: DialogActionType.SET_UPDATE_NOTES,
                            payload: '',
                          });
                        }
                      }}
                    />
                    <label htmlFor="sendEmailCheckbox" className="text-left">
                      {DIALOG_TEXT.WHETHER_TO_SEND_EMAIL}
                    </label>
                  </div>
                </div>
                <TextField
                  label={DIALOG_TEXT.UPDATE_NOTE_TEXT}
                  value={state.updateNotes}
                  disabled={!state.sendEmail}
                  onChange={e => {
                    dispatch({
                      type: DialogActionType.SET_UPDATE_NOTES,
                      payload: e.target.value,
                    });
                  }}
                  fullWidth
                ></TextField>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-row gap-8 py-4">
          <div className="flex-1 flex flex-col gap-4">
            <Button
              onClick={handleSupersede}
              disabled={state.supersedeLoading}
              variant="contained"
              size="large"
              sx={{ textTransform: 'none', width: '100%', height: '36px' }}
              startIcon={<RestartAlt />}
            >
              {state.supersedeLoading
                ? DIALOG_TEXT.LOADING
                : DIALOG_TEXT.SUPERSEDE_BUTTON}
            </Button>
            <Button
              onClick={handleClearForm}
              variant="contained"
              size="large"
              sx={{ textTransform: 'none', width: '100%', height: '36px' }}
            >
              {DIALOG_TEXT.CLEAR_BUTTON}
            </Button>
          </div>
          <div className="flex-1 flex flex-col gap-4">
            <Button
              onClick={handleRelease}
              disabled={state.releaseLoading || !supersededFile}
              variant="contained"
              size="large"
              sx={{ textTransform: 'none', width: '100%', height: '36px' }}
            >
              {state.releaseLoading
                ? DIALOG_TEXT.LOADING
                : DIALOG_TEXT.RELEASE_BUTTON}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default GSSupersedeOfferDialog;
